import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import CBAMAssistant from "@/components/assistant/CBAMAssistant";
import { MobileShell } from "@/components/mobile/MobileShell";
import TermsModal from "@/components/TermsModal";
import { usePageViewTracking } from "@/hooks/usePageViewTracking";
import { authRoutes } from "@/routes/authRoutes";
import { adviserPortalRoutes } from "@/routes/adviserPortalRoutes";
import { partnerOpsRoutes } from "@/routes/partnerOpsRoutes";
import { researchRoutes } from "@/routes/researchRoutes";
import { publicRoutes } from "@/routes/publicRoutes";
import { insightsRoutes } from "@/routes/insightsRoutes";

// AUDIENCE-SEPARATION (Phase 4 build-wall): AppInternal is the CANONICAL app — the DEFAULT entry
// (index.html → main-internal.tsx → AppInternal) that produces dist/index.html and is what the live
// Lovable pipeline builds/serves. It composes EVERY route fragment, including the internal-only
// research fragment. This is the byte-equivalent successor to the original monolithic src/App.tsx;
// the routing was decomposed into shared fragments (src/routes/*) so the adviser/public entries can
// compose only their audience's surfaces, but internal composes them all — behaviour is unchanged.

// Component to handle page view tracking inside Router context
const PageViewTracker = ({ children }: { children: React.ReactNode }) => {
  usePageViewTracking();
  return <>{children}</>;
};

const AppInternal = () => {
  // Create QueryClient inside component to avoid module-level instantiation issues.
  // Offline-resilience defaults (offline-resilience substrate): 'offlineFirst' lets the queryFn run
  // while offline so cached placeholders show immediately and reads resume on reconnect; capped
  // exponential retry/backoff; a 24h gcTime keeps inactive cache hydratable. These defaults are global
  // (this single client serves the desktop marketing site too) but are additive — 'offlineFirst'
  // behaves like the normal 'online' mode whenever the network is up, so desktop behaviour is unchanged.
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        networkMode: 'offlineFirst',
        refetchOnReconnect: true,
        retry: (failureCount: number) => failureCount < 3,
        retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000),
        staleTime: 30000,
        gcTime: 24 * 60 * 60 * 1000, // 24h
      },
      mutations: {
        networkMode: 'offlineFirst',
        retry: 2,
      },
    },
  }));

  // Pre-warm the offline cache mirror once at startup so getCachedSync() (React Query placeholderData)
  // hits before first paint. Guarded so it never blocks render and never throws on web.
  useEffect(() => {
    void import('@/lib/offline/persistentCache')
      .then(({ hydrateCache }) => hydrateCache())
      .catch(() => undefined);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <TermsModal />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <PageViewTracker>
            <CBAMAssistant />
            {/* Native-only 5-tab shell chrome; renders nothing on web (P0-IA-05). */}
            <MobileShell />
            <Routes>
              {/* Partner authentication + backward-compat redirects */}
              {authRoutes()}
              {/* Phase 3 — external adviser portal (F6). */}
              {adviserPortalRoutes()}
              {/* Internal partner/admin ops surfaces (non-research). */}
              {partnerOpsRoutes()}
              {/* PROPRIETARY research surface — INTERNAL ONLY. */}
              {researchRoutes()}
              {/* All other routes with layout (marketing + auth-gated insights). */}
              <Route path="/*" element={
                <Layout>
                  <Routes>
                    {insightsRoutes()}
                    {publicRoutes()}
                  </Routes>
                </Layout>
              } />
            </Routes>
          </PageViewTracker>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default AppInternal;
